import type { ComponentType, PropsWithChildren } from "react";
import { Link, type LinkProps } from "@tanstack/react-router";

import type { SVGProps } from "@/ui";
import { focusRingInset } from "@/ui";

type NavBarItemProps = LinkProps & {
  icon: ComponentType<SVGProps>;
  onClick?: () => void;
};

/** `sr-only`, not `hidden`: `hidden` would drop the label from the accessibility tree too. */
const NavBarItemLabel = ({ children }: PropsWithChildren) => {
  return <span className="sr-only lg:not-sr-only lg:block">{children}</span>;
};

export const NavBarItem = ({
  children,
  icon: Icon,
  to,
  ...props
}: PropsWithChildren<NavBarItemProps>) => {
  const routeHasHash = JSON.stringify(to).includes("#");

  return (
    <Link
      activeProps={{
        "aria-current": "page",
        className: !routeHasHash ? "border-content font-semibold text-content" : "",
      }}
      className={`flex h-full min-h-11 min-w-11 items-center justify-center gap-2 rounded-t-md border-b-4 px-2 transition-colors hover:border-line-strong hover:text-accent ${focusRingInset}`}
      inactiveProps={{ className: "border-transparent" }}
      to={to}
      {...props}
    >
      <NavBarItemLabel>{children}</NavBarItemLabel>

      <Icon aria-hidden="true" />
    </Link>
  );
};
